<template>
    <v-container fluid class="overflow-auto pa-5">
        <v-form :value="value" @input="$emit('input', $event)">
            <h4 class="mb-2">Inhaltliche Angaben</h4>
            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('bezeichnung')">
                        <v-text-field
                            :value="record?.title"
                            dense
                            outlined
                            hide-details="auto"
                            :counter="globalParam('magazin.max_title_chars', 100)"
                            :rules="[rules.required]"
                            @input="onInput('title', $event)"
                        ></v-text-field>
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('subtitle')">
                        <v-text-field
                            :value="record?.subtitle"
                            dense
                            hide-details="auto"
                            outlined
                            :counter="globalParam('magazin.max_subtitle_chars', 100)"
                            @input="onInput('subtitle', $event)"
                        ></v-text-field>
                    </LabelField>
                </v-col>
            </v-row>

            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('seo_title')">
                        <v-text-field
                            :value="record?.seo_title"
                            dense
                            hide-details="auto"
                            outlined
                            :counter="globalParam('magazin.max_seo_title_chars', 100)"
                            @input="onInput('seo_title', $event)"
                        ></v-text-field>
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('socialmedia_title')">
                        <v-text-field
                            :value="record?.socialmedia_title"
                            dense
                            hide-details="auto"
                            outlined
                            :counter="globalParam('magazin.max_socialmedia_title_chars', 100)"
                            @input="onInput('socialmedia_title', $event)"
                        ></v-text-field>
                    </LabelField>
                </v-col>
            </v-row>

            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('autor')">
                        <AutocompleteDisplay
                            :display-value="record?.autorDisplayTitle"
                            :collection="autorCollection"
                            item-text="displayTitle"
                            item-value="id"
                            query-param="query_name"
                            display-hide-details="auto"
                            @selected="onAutorSelect($event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6" class="d-flex">
                    <LabelField :label="$t('tags')" class="flex-grow-1">
                        <tag-select
                            for-magazin
                            :value="record?.tags"
                            show-download
                            show-add
                            hide-details="auto"
                            @input="onInput('tags', $event)"
                            @tag-added="onTagAdded"
                        ></tag-select>
                    </LabelField>
                </v-col>
            </v-row>

            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('rubriken')">
                        <genre-group-select
                            :value="record?.genre_group_ids"
                            :page-id="2"
                            clearable
                            :placeholder="$t('choose_multi')"
                            multiple
                            dense
                            outlined
                            hide-details="auto"
                            @input="onInput('genre_group_ids', $event)"
                        ></genre-group-select>
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6"> </v-col>
            </v-row>

            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('teaser')">
                        <v-textarea
                            :value="record?.lead_text"
                            hide-details="auto"
                            dense
                            outlined
                            :counter="globalParam('magazin.max_lead_chars', 100)"
                            @input="onInput('lead_text', $event)"
                        />
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6"> </v-col>
            </v-row>

            <h4 class="mb-2">Formales</h4>
            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('dossier')">
                        <dossier-select
                            :value="record?.dossier_ids"
                            clearable
                            hide-details="auto"
                            @input="onInput('dossier_ids', $event)"
                        ></dossier-select>
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('kachelgroesse')">
                        <kachelgroesse-select
                            :value="record?.overview_imagesize"
                            clearable
                            hide-details="auto"
                            @input="onInput('overview_imagesize', $event)"
                        ></kachelgroesse-select>
                    </LabelField>
                </v-col>
            </v-row>

            <v-row dense>
                <v-col cols="12" lg="6">
                    <LabelField :label="$t('order_number')">
                        <v-text-field
                            :value="record?.order_number"
                            type="number"
                            dense
                            outlined
                            hide-details="auto"
                            @input="onInput('order_number', $event)"
                        ></v-text-field>
                    </LabelField>
                </v-col>
                <v-col cols="12" lg="6"> </v-col>
            </v-row>

            <v-row dense>
                <v-col cols="12" lg="6" class="d-flex">
                    <v-checkbox
                        :value="record?.show_disqus"
                        :input-value="record?.show_disqus"
                        :label="$t('show_comments')"
                        class="mr-3"
                        dense
                        @change="onInput('show_disqus', $event)"
                    />
                    <v-checkbox
                        :value="record?.has_comments"
                        :input-value="record?.has_comments"
                        :label="$t('has_comments')"
                        class="mr-3"
                        dense
                        @change="onInput('has_comments', $event)"
                    />
                    <v-checkbox
                        :value="record?.has_photo_gallery"
                        :input-value="record?.has_photo_gallery"
                        :label="$t('has_photo_gallery')"
                        dense
                        @change="onInput('has_photo_gallery', $event)"
                    />
                </v-col>
                <v-col cols="12" lg="6" class="d-flex">
                    <v-checkbox
                        :value="record?.has_video"
                        :input-value="record?.has_video"
                        :label="$t('has_video')"
                        class="mr-3"
                        dense
                        @change="onInput('has_video', $event)"
                    />
                    <v-checkbox
                        :value="record?.add_fb_js_sdk"
                        :input-value="record?.add_fb_js_sdk"
                        :label="$t('add_js_sdk')"
                        dense
                        @change="onInput('add_fb_js_sdk', $event)"
                    />
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>

<script>
import { Magazin } from '@/models/Magazin';
import { formRules } from '@/helpers/tools';
import TagSelect from '@/components/tag/select.vue';
import GenreGroupSelect from '@/components/genre_group/select.vue';
import DossierSelect from '@/components/dossier/select.vue';
import KachelgroesseSelect from '@/components/magazin/kachelgroesse_select.vue';
import { AddressCollection } from '@/models/Address';
import GlobalsMixin from '@/mixins/globals';

export default {
    name: 'MagazinEditBasisdaten',
    components: {
        TagSelect,
        GenreGroupSelect,
        DossierSelect,
        KachelgroesseSelect,
    },
    mixins: [GlobalsMixin],
    props: {
        record: {
            type: Magazin,
            required: true,
        },
        // used to indicate form validity
        value: Boolean,
    },
    data() {
        const autorCollection = new AddressCollection();
        autorCollection.addExtraParam('filter', [
            { property: 'type', value: 'address' },
            { property: 'is_active', operator: 'IS', value: true },
        ]);
        return {
            autorCollection: autorCollection,
            isValid: false,
        };
    },
    created() {
        this.rules = formRules;
    },
    methods: {
        onInput(field, value) {
            this.record?.set(field, value);
        },

        onAutorSelect(rec) {
            // Nach dem Autor-Select setzen wir sowohl den Display-Wert (damit man gleich sieht
            // was gewählt wurde), sowie die ID der Adresse:
            if (rec) {
                this.onInput('autor_address_id', rec.id);
                this.onInput('autor_title', rec.title);
                this.onInput('autor_name', `${rec.first_name || ''} ${rec.surname || ''}`.trim() || '');
            } else {
                this.onInput('autor_address_id', null);
                this.onInput('autor_title', null);
                this.onInput('autor_name', null);
            }
        },

        onTagAdded(tag) {
            let tags = [...this.record?.tags];
            tags.push(tag.id);
            this.onInput('tags', tags);
        },
    },
};
</script>
